import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Audio } from 'react-loader-spinner';
import { MdOutlineCancel } from "react-icons/md";
import { BsBank } from "react-icons/bs";
import { HiOutlineReceiptTax } from "react-icons/hi";
import { FaRegBuilding } from "react-icons/fa";
import { FaUserCircle } from "react-icons/fa";
import { MdAccountCircle } from "react-icons/md";
import {Layout, EmployeeProfile, DeleteConfig} from '../../../components';
import {handleApi,inputDate} from '../../../utils/utils';
import AppContext from '../../../contexts/AppContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DeleteEmployee = () => {
  const location = useLocation();
  const initialEmployee = location.state?.initialEmployee;
  // FIX: Initialize react-hook-form directly with defaultValues from initialEmployee.
  // This removes the need for a separate 'employee' state variable that was causing conflicts.
  const { reset, control, register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: initialEmployee
  });
  
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [displayImg, setDisplayImg] = useState(''); 
  const [imgType, setImgType] = useState('');
  const { userData, companyProfile, bank, currentColor, actionCount, setActionCount } = useContext(AppContext);


  
  // FIX: This useEffect now only runs once on component mount to handle the initial photo.
  // It is no longer a dependency on 'employee' state, which was causing the reset loop.
  useEffect(() => {
    if (initialEmployee?.photo_url) {
      fetchImageAsBase64(initialEmployee.photo_url);
    } else if (initialEmployee?.photo?.data && initialEmployee?.photo?.contentType) {
      setDisplayImg(initialEmployee.photo.data);
      setImgType(initialEmployee.photo.contentType);
    } else {
      setDisplayImg('');
      setImgType('');
    }
  }, [initialEmployee]);
  
  // New function to fetch image from URL and convert to base64
  const fetchImageAsBase64 = async (url) => {
    setIsImageLoading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result.split(',')[1];
        const contentType = blob.type;
        setDisplayImg(base64data);
        setImgType(contentType);
      };
    } catch (error) {
      console.error("Failed to fetch image from URL", error);
      setDisplayImg('');
      setImgType('');
    } finally {
      setIsImageLoading(false);
    }
  };
  
  
  const handleCancel = () => {
    // FIX: reset form and image states to initial values
    reset(initialEmployee);
    
  };

  const navigate = useNavigate(); 

  const handleClosePage = () => {
   navigate(-1);
  };

  const convertFile = (files) => {
    if (files && files.length > 0 && files[0] instanceof File) {
      const fileRef = files[0];
      const fileType = fileRef.type;
      const reader = new FileReader();
      reader.readAsBinaryString(fileRef);
      reader.onload = (ev) => {
        const base64Data = btoa(ev.target.result);
        setDisplayImg(base64Data);
        setImgType(fileType);
        // FIX: Remove the setEmployee call to prevent the useEffect from triggering a reset.
      };
    }
  };

  
  
  const handleEmployeeProfileFormSubmit = async (dataFromForm) => {

    try {
    //   console.log('Form data received from react-hook-form (safe stringify):', JSON.stringify(dataFromForm, null, 2));
    } catch (e) {
      console.error("Failed to stringify `dataFromForm`. Circular reference found:", dataFromForm);
      toast.error("Form data is corrupted. Check the console for details.");
      setIsLoading(false);
      return;
    }
 
    const payload = {    // Create payload 
        id: dataFromForm.id,
        deactivate_reason: dataFromForm.deactivate_reason,
        deactivate_date:dataFromForm.deactivate_date,
        status: 'inactive',
    };
   
    try {
      console.log('Final payload to send to API (safe stringify):', JSON.stringify(payload, null, 2));
    } catch (e) {
      console.error("Final payload has a circular reference. Inspect the object below.", payload);
      toast.error("The final payload is corrupted. Check the console for details.");
      setIsLoading(false);
      return;
    }

    const apiMethod = payload.id ? 'PUT' : 'POST';
    const parameters = { ...(payload.id && { id: payload.id }) };
    setIsLoading(true);
    try {
      const response = await handleApi('employees', payload, userData, apiMethod, parameters);
      if (response.status === 200 || response.status === 201) {
        toast.success('Employee profile saved successfully!');
        // FIX: The component will now display the updated data without a full reset.
        // The parent state no longer needs to be explicitly updated here.
      } else {
        toast.error(`Error saving employee: ${response.statusText || 'An unknown error occurred'}`);
      }
    } catch (error) {
      toast.error('Error saving employee: ' + error.message);
    } finally {
      setActionCount(actionCount + 1);
      setIsLoading(false);
    }
  };
  const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 512 512" fill="${currentColor}"><path d="M256,0C114.6,0,0,114.6,0,256s114.6,256,256,256s256-114.6,256-256S397.4,0,256,0z M256,79.5c37.5,0,67.9,30.4,67.9,67.9S293.5,215.4,256,215.4S188.1,185,188.1,147.4S218.5,79.5,256,79.5z M256,441.6c-70.5,0-134.4-29.3-177.3-76.3c-0.3-0.3-0.5-0.6-0.8-1c-0-0-0-0-0-0.1C72,322,112.5,274.6,161.4,274.6h189.1c48.9,0,89.4,47.4,83.6,90.5c-0,0-0,0-0.1,0.1c-0.3,0.3-0.5,0.7-0.8,1C390.4,412.3,326.5,441.6,256,441.6z" /></svg>`;
  const base64Svg = btoa(unescape(encodeURIComponent(svgString)));
  const defaultPhotoUrl = `data:image/svg+xml;base64,${base64Svg}`;

  const photoUrl = isImageLoading ? null : (displayImg && imgType ? `data:${imgType};base64,${displayImg}` : defaultPhotoUrl);
  return (
    <Layout>
      <div className="m-1 sm:m-2">
        <div className="relative flex justify-between items-center">
          <h1 className="text-center font-bold flex-grow">DEACTIVATE EMPLOYEE</h1>
          <button
            onClick={handleClosePage}
            className="p-1 rounded-lg text-red-400 bg-white hover:bg-gray-50 hover:text-gray-600"
          >
            <MdOutlineCancel color={currentColor} size={30} />
          </button>
        </div>
        
        <ToastContainer position="top-center" autoClose={2000} />

        {isLoading ? (
          <div className="flex items-center flex-col py-6">
            <Audio height="80" width="80" radius="9" color={currentColor} ariaLabel="loading" />
            <p className="mt-2 text-white">Loading. Please wait...</p>
          </div>
        ) : (
          <div className="bg-white p-2 sm:p-4 my-4 my-auto mx-auto max-w-[80%] rounded-xl table">
            <div className="mx-auto border-b border-gray-400 bg-gray-500 transition-all duration-200 cursor-pointer rounded-xl  p-8 table ">
              <EmployeeProfile
                  fields={DeleteConfig}
                  control={control}
                  register={register}
                  photo={photoUrl}
                //   onChange={handleChange}
                  departments={departments}

                  onSubmit={handleSubmit(handleEmployeeProfileFormSubmit)}
                  employeeData={initialEmployee} // FIX: Pass initialEmployee here instead of a mutable state variable.
                  onCancel={handleCancel}
                  errors={errors}
                  reset={reset}
                  isLoading={isLoading}
                  deleting={true} //Pass the boolean true .
                />
              </div>
            
          </div>
        )}
      </div>
    </Layout>
  );
};
export default DeleteEmployee;
